import React, { useEffect, useRef, useState } from 'react';
import { AuraSimplePageRoot, isAdminOrStaff, isCustomer, isSupplier } from 'app/main/apps/common/AuraFunctions';
import AuraHeader from 'app/main/apps/common/AuraHeader';
import { useReactToPrint } from 'react-to-print';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from 'app/store/userSlice';
import { labelsHaveBoxCodes } from 'app/main/apps/scanToLoad/scanToLoadRules';
import ShippingLabelDetails from '../tabs/ShippingLableDetails';
import { effectiveLabelStyle, isBoxLabelPrint, readLabelStyle, writeLabelStyle } from '../tabs/labelStyle';

function labelStorage() {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

const ShippingLabelContent = () => {
    const [isPrinting, setIsPrinting] = useState(false);
    const user = useSelector(selectUser);
    const labelRef = useRef()
    const promiseResolveRef = useRef(null);
    const location = useLocation()
    const navigate = useNavigate()
    const { orderId } = useParams()
    // PGSS-5748 part 3: true once the printed labels carry box codes (a supplier container).
    const [canScanToLoad, setCanScanToLoad] = useState(false)
    const { pathname } = location
    const pathType = pathname.split('/')[1];
    const pathType2 = pathname.split('/')[2];
    const isOutboundPrintLabel = pathType === "outbound" && pathType2 === "print-label";
    const isInboundPrintLabel = pathType === "shipments" && pathType2 === "print-label";
    const isprintProduction = pathType2 === "printProduction" || pathType2 === "print-label"
    // PGSS-5748: the supplier's box labels print one 4" × 6" label per page; "Classic label" prints the label as it was
    // before, on the A3 sheet below, as a fallback.
    const [labelStyle, setLabelStyle] = useState(() => readLabelStyle(labelStorage()));
    const isBoxLabel = isBoxLabelPrint(pathType2, labelStyle)
    // PGSS-5748: Scan to load is offered on the supplier's label page whichever label is chosen (classic or box).
    const isSupplierLabelPage = pathType2 === "shippingLabel"
    const handleLabelStyleChange = (style) => {
        setLabelStyle(style);
        writeLabelStyle(labelStorage(), style);
    };
    const isAdminOrStaffUser = isAdminOrStaff(user)
    const isCustomerUser = isCustomer(user?.role)
    const isSupplierUser = isSupplier(user?.role)

    useEffect(() => {
        if (isPrinting && promiseResolveRef.current) {
            // Resolves the Promise, letting `react-to-print` know that the DOM updates are completed
            promiseResolveRef.current();
        }
    }, [isPrinting]);

    const getValue = (valueFor) => {
        if ((isAdminOrStaffUser || isCustomerUser) && isInboundPrintLabel) {
            if(valueFor === "title") return "Packing Slip";
            if(valueFor === "button_name") return  "Print";
            return 'packing-slip.pdf'
        }
        if (isAdminOrStaffUser && isOutboundPrintLabel) {
            if(valueFor === "title") return "Pick Ticket";
            if(valueFor === "button_name") return  "Print";
            return 'pick-ticket.pdf'
        }
        if (isprintProduction) {
            if(valueFor === "title") return "Print Production Sheet";
            if(valueFor === "button_name") return  "Download";
            return 'print-production-sheet.pdf'
        }
        if(valueFor === "title") return "Shipment Label";
        if(valueFor === "button_name") return  "Download";
        return 'shipping-label.pdf';
    };

    // PGSS-5148: react-to-print v3 (contentRef) — v2 used findDOMNode, removed in React 19.
    const handleLabelDownload = useReactToPrint({
        contentRef: labelRef,
        onBeforePrint: () => {
            return new Promise((resolve) => {
                promiseResolveRef.current = resolve;
                setIsPrinting(true);
            });
        },
        onAfterPrint: () => {
            // Reset the Promise resolve so we can print again
            promiseResolveRef.current = null;
            setIsPrinting(false);
        },
        pageStyle: isBoxLabel ? `
        @page {
            size: 4in 6in;
            margin: 0;
        }
        @media print {
            body {
                margin: 0;
                padding: 0;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
        }
        ` : pathType2 === "print-label" ? `
        @media print {
            body {
                padding: 5px;
                font-size: 12px;
                -webkit-print-color-adjust: exact;
              }
            .scale-content {
              transform: scale(0.6); /* Scale content to 60% */
              transform-origin: top left; /* Set transformation origin */
            }
            .element-with-spacing {
                margin-bottom: 20mm!important; /* Add spacing between elements */
                page-break-inside: avoid!important; /* Avoid breaking the element between pages */
              }
          }
        `  :  `
        @page {
            size: A3 landscape;
        }
        @media print {
            body {
                padding: 20px;
                font-size: 12px;
                -webkit-print-color-adjust: exact;
                }
            .scale-content {
                transform: scale(0.6); /* Scale content to 60% */
                transform-origin: top left; /* Set transformation origin */
            }
            .element-with-spacing {
                margin-bottom: 20mm!important; /* Add spacing between elements */
                page-break-inside: avoid!important; /* Avoid breaking the element between pages */
                }
            }
        `,
        documentTitle: getValue("document_title"),
    });

    const getLink = () => {
        if(isAdminOrStaffUser && isOutboundPrintLabel)
            return '/shipments/calendar'
        if(isAdminOrStaffUser && isInboundPrintLabel)
            return '/shipments/containers'
        // PGSS-5805: staff reach the supplier's label page from Print box labels; back goes to their containers, not
        // to the supplier's In Production.
        if(isAdminOrStaffUser && isSupplierLabelPage)
            return '/shipments/containers'
        if(isCustomerUser && isInboundPrintLabel)
            return '/container-shipments'
        return  '/in-production'
    }

    const headerData = {
        headerTitle: getValue("title"),
        filter: {
            component: null,
        },
        search: {
            enabled: false,
        },
        updateShow: true,
        showNavigate: pathType2 === "printProduction",
        links: [
            {
                id: 1,
                title: getValue("button_name"),
                type: 'save',
                value: 'Download',
                show: true,
                onClick: () => handleLabelDownload(),
            },
            {
                id: 2,
                type: 'back',
                value: 'Shipments',
                link: getLink(),
                show: pathType2 !== "printProduction",
            }
        ],
        // PGSS-5748 part 3: the way into Scan to load, shown only on the box labels of a container with box codes.
        // PGSS-5805: staff open this page from Print box labels too; Scan to load is the supplier's step, so only they get it.
        extraButtons: [
            {
                id: 'scan-to-load',
                name: 'Scan to load',
                icon: 'qr_code_scanner',
                color: 'secondary',
                testId: 'scan-to-load-entry',
                show: isSupplierLabelPage && isSupplierUser && canScanToLoad,
                onBtnClick: () => navigate(`/shipments/scan-to-load/${orderId}`),
            },
        ],
        toComponentState: {from: 'pickTicket'},
        headerClassName: "flex-col sm:flex-row !py-12"
    };

    return <AuraSimplePageRoot
        header={<AuraHeader {...headerData} />}
        content={
          <ShippingLabelDetails
            labelRef={labelRef}
            isPrinting={isPrinting}
            isprintProduction={!!isprintProduction}
            isAdminOrStaffUser={!!isAdminOrStaffUser}
            isCustomerUser={isCustomerUser}
            isSupplierUser={isSupplierUser}
            onLabelData={isSupplierLabelPage ? (payload) => setCanScanToLoad(labelsHaveBoxCodes(payload)) : undefined}
            // PGSS-5748: only the supplier Shipping Label page offers (and honours) the Classic label choice; the
            // order dialog and the other label routes always show the box label.
            labelStyle={effectiveLabelStyle(labelStyle, isSupplierLabelPage)}
            onLabelStyleChange={isSupplierLabelPage ? handleLabelStyleChange : undefined}
          />
        }
        sidebarInner
    />
};

export default ShippingLabelContent;
